///<reference path="processEditor-module.ts"/>
///<reference path="scopeEditorController.ts"/>

module ProcessEditor {
    'use strict';

    export interface INodeWithExecutionData extends INode {
        executionData: ProcessInstanceStep;
    }

    export class TaskNodeWithExecutionData extends TaskNode implements INodeWithExecutionData {
        public executionData: ProcessInstanceStep;
    }
    export class DecisionNodeWithExecutionData extends DecisionNode implements INodeWithExecutionData {
        public executionData: ProcessInstanceStep;
    }
    export class ReturnNodeWithExecutionData extends ReturnNode implements INodeWithExecutionData {
        public executionData: ProcessInstanceStep;
    }
    export class ThrowErrorNodeWithExecutionData extends ThrowErrorNode implements INodeWithExecutionData {
        public executionData: ProcessInstanceStep;
    }
    export class ForeachScopeNodeContainer extends ForeachScopeNode implements INodeWithExecutionData {
        public executionData: ProcessInstanceStep;
        constructor(node: ForeachScopeNode, public foreachExecutionNodes: ForeachScopeNodeWithExecutionData[]) {
            super(node);
        }
    }
    export class ForeachScopeNodeWithExecutionData extends ForeachScopeNode implements INodeWithExecutionData {
        public executionData: ProcessInstanceStep;
        constructor(node: ForeachScopeNode, public index: number) {
            super(node);
        }
    }

    export class ErrorScopeNodeWithExecutionData extends ErrorScopeNode implements INodeWithExecutionData {
        public executionData: ProcessInstanceStep;
    }

    export class ProcessInstanceDetailsController {
        public processName: string;
        public instance: ProcessInstance;
        public node: ScopeNode;

        private getNodeEnrichedWithExecutionData(node: Node, steps: ProcessInstanceStep[], filters: IFilterHash) {
            if (!node) {
                return node;
            }

            function getFilterKey(foreachNodeId: string): string {
                return `$loop_${foreachNodeId}_index`
            }

            function matchesFilters(data: ProcessInstanceStep): boolean {
                function foreachFilterMatches(data: ProcessInstanceStep, filter: IExecutionDataFilter) {
                    if (filter.type === "foreachIndexFilter" && data.parameters) {
                        let itemIndex = data.parameters[filter.key];
                        return itemIndex === filter.value;
                    }
                    return false;
                }
                return _.isEmpty(filters) || _.every(filters, f => foreachFilterMatches(data, f));
            }

            function getEnrichedNode<T extends INodeWithExecutionData>(node: T): T {
                let stepData = _.find(steps, s => s.stepId === node.nodeId && matchesFilters(s));
                if (stepData) {
                    node.executionData = stepData;
                }
                return node;
            }

            function processScopeSubNodesAndErrorHandlers(scopeNode: ScopeNode, ctrl: ProcessInstanceDetailsController): ScopeNode {
                scopeNode.nodes = scopeNode.nodes.map(n => ctrl.getNodeEnrichedWithExecutionData(n, steps, filters));
                if (scopeNode.errorHandler) {
                    scopeNode.errorHandler = <ErrorScopeNode>ctrl.getNodeEnrichedWithExecutionData(scopeNode.errorHandler, steps, filters);
                }
                return scopeNode
            }

            if (node instanceof TaskNode) {
                let taskNode = new TaskNodeWithExecutionData(node);
                if (taskNode.errorHandler) {
                    taskNode.errorHandler = <ErrorScopeNode>this.getNodeEnrichedWithExecutionData(taskNode.errorHandler, steps, filters);
                }
                return getEnrichedNode(taskNode);

            } else if (node instanceof DecisionNode) {
                let decisionNode = new DecisionNodeWithExecutionData(node);
                return getEnrichedNode(decisionNode);

            } else if (node instanceof ReturnNode) {
                let returnNode = new ReturnNodeWithExecutionData(node);
                return getEnrichedNode(returnNode);

            } else if (node instanceof ThrowErrorNode) {
                let throwErrorNode = new ThrowErrorNodeWithExecutionData(node);
                return getEnrichedNode(throwErrorNode);

            } else if (node instanceof ScopeNode) {
                let scopeNode = <ScopeNode>node;
                if (node instanceof ForeachScopeNode) {
                    let filterKey = getFilterKey(node.nodeId);
                    let indexesForNode = _.map(steps, d => {
                        return d.parameters && d.parameters[filterKey];
                    });
                    let foreachExecutions = [];
                    for (var i = 0; i <= _.max(indexesForNode); i++) {
                        var subFilters = _.extend({}, filters);
                        subFilters[filterKey] = { type: "foreachIndexFilter", key: filterKey, value: i };
                        var subNode = getEnrichedNode(new ForeachScopeNodeWithExecutionData(node, i));
                        subNode.nodes = scopeNode.nodes.map(n => this.getNodeEnrichedWithExecutionData(n, steps, subFilters));
                        if(subNode.errorHandler){
                          subNode.errorHandler = <ErrorScopeNode>this.getNodeEnrichedWithExecutionData(subNode.errorHandler, steps, subFilters);
                        }

                        foreachExecutions.push(subNode);
                    }
                    scopeNode = getEnrichedNode(new ForeachScopeNodeContainer(node, foreachExecutions));
                } else if (node instanceof ErrorScopeNode) {
                    scopeNode = processScopeSubNodesAndErrorHandlers(getEnrichedNode(new ErrorScopeNodeWithExecutionData(node)), this);
                }
                else {
                    // just plain scope node
                    scopeNode = processScopeSubNodesAndErrorHandlers(scopeNode, this);
                }
                return scopeNode;

            } else {
                return node;
            }
        }
        static $inject = ["$scope", "$timeout", "$rootScope", "ProcessService", "ProcessInstanceService", "$q", "executionId", "processGuid", "processVersion", "$window", "graphEditDisabled"];
        constructor($scope: IScopeEditorScope,
            $timeout: ng.ITimeoutService,
            $rootScope: ng.IRootScopeService,
            private processService: ProcessService,
            private processInstanceService: ProcessInstanceService,
            $q: ng.IQService,
            executionId: string,
            processGuid: string,
            processVersion: number,
            private $window: ng.IWindowService,
            public graphEditDisabled = true) {

            $rootScope.$broadcast("loadingspinner.manual.spin");
            var instanceRequest = processInstanceService.getProcessInstance(executionId);
            var processRequest = processService.getData(processGuid, processVersion);
            $q.all([instanceRequest, processRequest])
                .then(results => {
                    var instance = <ProcessInstance>results[0];
                    var process = <Process>results[1];

                    this.processName = process.name;
                    this.$window.document.title = FRENDS.Localizations.Show + " " + process.name + " - " + process.majorVersion + "." + process.minorVersion + "." + process.version;
                    this.instance = instance;
                    this.node = <ScopeNode>this.getNodeEnrichedWithExecutionData(process.rootScope, instance.steps, {})
                }, (error) => {
                    console.error("Error when fetching data", error);
                }).then(() => {
                    $rootScope.$broadcast('loadingspinner.manual.stop');
                });
        }
    }

    app.controller("ProcessInstanceDetailsController", ProcessInstanceDetailsController);

    interface IFilterHash {
        [key: string]: IExecutionDataFilter;
    }

    interface IExecutionDataFilter {
        type: string;
        key: string;
        value: string | number;
    }
}
